import {
  FETCH_ASSIGNED_CREATIVES,
  APPEND_ASSIGNED_CREATIVES,
  COMPLETE_ASSIGNED_CREATIVES,
  PAUSE_ASSIGNED_CREATIVES,
  RESUME_ASSIGNED_CREATIVES
} from "./constants";
import { SUCCESS, FAILURE, PENDING } from "../constants";

import { fetchAssignedCreativesService } from "../../services/dcm/creatives";
import { setAuthError } from "../app/global";

// Fingerprint that identifies the conditions of a fetch. The reducer
// stores it so the next picker open can compare and decide whether to
// reuse the cached list or refetch.
export const buildAssignedCreativesFetchKey = (creativeTypes = [], sizes = [], advertiserId = "") => {
  const types = [...(creativeTypes || [])].sort().join(",");
  const sortedSizes = [...(sizes || [])].sort().join(",");
  return `${types}|${sortedSizes}|${advertiserId || ""}`;
};

const beginFetchAssignedCreatives = (
  campaignId,
  creativeTypes,
  sizes,
  advertiserId
) => ({
  type: FETCH_ASSIGNED_CREATIVES,
  status: PENDING,
  campaignId,
  creativeTypes,
  sizes,
  advertiserId,
  fetchKey: buildAssignedCreativesFetchKey(creativeTypes, sizes, advertiserId)
});

const successAssignedCreatives = (creatives, nextPageToken) => ({
  type: FETCH_ASSIGNED_CREATIVES,
  status: SUCCESS,
  data: creatives,
  nextPageToken
});

const appendAssignedCreatives = (creatives, nextPageToken) => ({
  type: APPEND_ASSIGNED_CREATIVES,
  data: creatives,
  nextPageToken
});

const completeAssignedCreatives = () => ({
  type: COMPLETE_ASSIGNED_CREATIVES
});

const pauseAssignedCreatives = (nextPageToken) => ({
  type: PAUSE_ASSIGNED_CREATIVES,
  nextPageToken
});

const beginResumeAssignedCreatives = () => ({
  type: RESUME_ASSIGNED_CREATIVES
});

const failureAssignedCreatives = err => ({
  type: FETCH_ASSIGNED_CREATIVES,
  status: FAILURE,
  err
});

// Inner paging loop, shared by fresh fetch and resume. Reads/writes
// `nextPageToken` in outer scope so the catch + abort branches can record
// where to resume from.
const runAssignedCreativesPagingLoop = async (
  dispatch,
  campaignId,
  creativeTypes,
  sizes,
  advertiserId,
  signal,
  startingPageToken
) => {
  let nextPageToken = startingPageToken;
  try {
    while (nextPageToken) {
      if (signal && signal.aborted) {
        dispatch(pauseAssignedCreatives(nextPageToken));
        return;
      }

      const result = await fetchAssignedCreativesService(
        campaignId,
        creativeTypes,
        sizes,
        advertiserId,
        nextPageToken,
        signal
      );

      if (signal && signal.aborted) {
        // Abort fired during the request — the token we tried is still the
        // one we haven't successfully appended. Save it.
        dispatch(pauseAssignedCreatives(nextPageToken));
        return;
      }

      if (result.creatives && result.creatives.length > 0) {
        dispatch(appendAssignedCreatives(result.creatives, result.nextPageToken));
      }

      nextPageToken = result.nextPageToken;
    }
    dispatch(completeAssignedCreatives());
  } catch (e) {
    if (e && (e.name === "AbortError" || (signal && signal.aborted))) {
      dispatch(pauseAssignedCreatives(nextPageToken));
      return;
    }
    if (e.message === "UNAUTHORIZED") {
      dispatch(setAuthError());
      return;
    }
    dispatch(failureAssignedCreatives(e));
  }
};

export const fetchAssignedCreatives = (
  campaignId,
  creativeTypes,
  sizes,
  advertiserId,
  signal
) => {
  return async dispatch => {
    dispatch(
      beginFetchAssignedCreatives(
        campaignId,
        creativeTypes,
        sizes,
        advertiserId
      )
    );

    let firstResult;
    try {
      firstResult = await fetchAssignedCreativesService(
        campaignId,
        creativeTypes,
        sizes,
        advertiserId,
        null,
        signal
      );
    } catch (e) {
      if (e && (e.name === "AbortError" || (signal && signal.aborted))) {
        dispatch(pauseAssignedCreatives(null));
        return;
      }
      if (e.message === "UNAUTHORIZED") {
        dispatch(setAuthError());
        return;
      }
      dispatch(failureAssignedCreatives(e));
      return;
    }

    if (signal && signal.aborted) {
      dispatch(pauseAssignedCreatives(firstResult.nextPageToken));
      return;
    }

    dispatch(successAssignedCreatives(firstResult.creatives || [], firstResult.nextPageToken));

    await runAssignedCreativesPagingLoop(
      dispatch,
      campaignId,
      creativeTypes,
      sizes,
      advertiserId,
      signal,
      firstResult.hasMore ? firstResult.nextPageToken : null
    );
  };
};

// Continue paging from a previously-saved nextPageToken. Used when the
// user reopens the picker after accidentally closing it mid-load.
export const resumeAssignedCreatives = (
  campaignId,
  creativeTypes,
  sizes,
  advertiserId,
  signal,
  startingPageToken
) => {
  return async dispatch => {
    dispatch(beginResumeAssignedCreatives());
    await runAssignedCreativesPagingLoop(
      dispatch,
      campaignId,
      creativeTypes,
      sizes,
      advertiserId,
      signal,
      startingPageToken
    );
  };
};

export const clearAssignedCreatives = () => {
  return async dispatch => {
    dispatch(successAssignedCreatives([]));
  }
}

// Fingerprint for a multi-scope fetch (see fetchAssignedCreativesForScopes).
export const buildMultiScopeFetchKey = (scopes = [], advertiserId = "") => {
  const scopeKey = [...scopes]
    .map((s) => buildAssignedCreativesFetchKey(s.creativeTypes, s.sizes, ""))
    .sort()
    .join("§");
  return `${scopeKey}||${advertiserId || ""}`;
};

// Fetch several independent (creativeTypes, sizes) scopes into the same
// picker list. CM360's creatives.list filter ANDs `types` with `sizeIds` in
// a single request — there's no way to ask for "DISPLAY at size X, OR any
// INSTREAM_VIDEO, OR any INSTREAM_AUDIO" in one call. Smart Upload needs
// exactly that when placements of mixed compatibility/size are selected,
// so this runs one paginated fetch per scope, sequentially, appending all
// results into the same reducer state.
//
// Trade-off vs. fetchAssignedCreatives/resumeAssignedCreatives: this does
// not persist per-scope resume state. Closing the dialog mid-load and
// reopening restarts from the first scope. Acceptable here because each
// scope is now size/type-narrowed, so a full reload is fast.
export const fetchAssignedCreativesForScopes = (
  campaignId,
  scopes,
  advertiserId,
  signal
) => {
  return async dispatch => {
    const fetchKey = buildMultiScopeFetchKey(scopes, advertiserId);

    dispatch({
      type: FETCH_ASSIGNED_CREATIVES,
      status: PENDING,
      campaignId,
      advertiserId,
      fetchKey
    });

    let firstPageDispatched = false;

    try {
      for (const scope of scopes) {
        if (signal && signal.aborted) return;

        let pageToken = null;
        let hasMore = true;

        while (hasMore) {
          if (signal && signal.aborted) return;

          const result = await fetchAssignedCreativesService(
            campaignId,
            scope.creativeTypes,
            scope.sizes,
            advertiserId,
            pageToken,
            signal
          );

          if (signal && signal.aborted) return;

          const pageCreatives = result.creatives || [];
          hasMore = !!result.hasMore;
          pageToken = result.nextPageToken;

          if (!firstPageDispatched) {
            dispatch(successAssignedCreatives(pageCreatives, hasMore ? pageToken : null));
            firstPageDispatched = true;
          } else if (pageCreatives.length > 0) {
            dispatch(appendAssignedCreatives(pageCreatives, hasMore ? pageToken : null));
          }
        }
      }

      if (!firstPageDispatched) {
        dispatch(successAssignedCreatives([], null));
      }

      dispatch(completeAssignedCreatives());
    } catch (e) {
      if (e && (e.name === "AbortError" || (signal && signal.aborted))) {
        return;
      }
      if (e.message === "UNAUTHORIZED") {
        dispatch(setAuthError());
        return;
      }
      dispatch(failureAssignedCreatives(e));
    }
  };
};
